import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import {
  acceptanceTerms,
  brokerQuotes,
  brokers,
  contractualTerms,
  operations,
  policyholders,
  type BrokerQuoteEstimateInput,
  type OperationDetail,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatMoney,
  formatRate,
  parseMoney,
  rateToBasisPoints,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody } from '@/shared/ui/Card';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Skeleton, SkeletonText } from '@/shared/ui/Skeleton';
import { Stepper } from '@/shared/ui/Stepper';
import { useToast } from '@/shared/ui/useToast';

import { sugerirComissao } from '../sugestoesDaOperacao';
import {
  AVISO_TAGS_NAO_PREENCHIDAS,
  chaveDosTermos,
  temTagVaziaNoBlocoHabilitado,
} from '../termosDaCotacao';

import {
  passoPedeCotacao,
  passosDoAssistente,
  passoValido,
  type PassoDoAssistente,
} from './cotacaoDaCorretora';
import { PremiumEstimateBanner } from './PremiumEstimateBanner';
import { ObjetoStep, PagamentoStep, ResumoStep } from './QuoteWizardFinish';
import { RiscoStep, SeguradoStep, TomadorStep } from './QuoteWizardSteps';
import {
  entradaDaAbertura,
  entradaDaCotacao,
  faltasDoPasso,
  rascunhoDaOperacao,
  rascunhoVazio,
  type RascunhoDaCotacao,
} from './rascunho';

const linkSecundario =
  'inline-flex h-10 items-center rounded-md px-3 text-sm font-medium text-ink-600 transition-colors duration-(--duration-fast) hover:bg-ink-100';

/**
 * Assistente de nova cotacao PJ da corretora (`/propostas/nova` do OnPoint; NCT-059 a
 * NCT-180), em passos: tomador, segurado, risco, objeto, pagamento e resumo, um por rota
 * filha (`/operacoes/nova/:passo` e `/operacoes/:operationId/editar/:passo`).
 *
 * A cotacao e gravada ao fim do passo do risco, em em_cotacao; os passos seguintes
 * editam a cotacao gravada, e o resumo a envia para analise. O premio estimado fica no
 * topo e e o que o servidor calcula hoje (IS x taxa), sem gravar. O assistente e da
 * corretora (decisao 1 da 11.11): a seguradora recebe o aviso e abre pela lista.
 */
export function NewQuoteWizard() {
  const { session } = useAuth();
  if (session?.broker_scoped !== true) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Nova cotacao" />
        <Alert tone="info" title="Tela da corretora">
          <div className="flex flex-col gap-3">
            <p>
              O assistente de nova cotacao e da corretora. A seguradora abre a operacao
              pela lista de operacoes, em Nova operacao.
            </p>
            <Link
              to="/operacoes"
              className="font-medium text-brand-700 hover:underline"
            >
              Ir para a lista de operacoes
            </Link>
          </div>
        </Alert>
      </div>
    );
  }
  return <AssistenteDaCorretora />;
}

function AssistenteDaCorretora() {
  const { operationId = '', passo: passoDaRota } = useParams();
  const passo = passoValido(passoDaRota);
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const nova = operationId === '';

  const opcoes = useQuery({
    queryKey: ['broker-quote-options'],
    queryFn: ({ signal }) => brokerQuotes.options(signal),
  });
  // A corretora da sessao, com o percentual e o limite de comissao dela: a sugestao da
  // comissao e a mesma da Nova operacao (OPEN_QUESTIONS 2.97). Sem a leitura, a cotacao
  // abre sem comissao, como antes.
  const corretora = useQuery({
    queryKey: ['brokers', 'current'],
    queryFn: ({ signal }) => brokers.current(signal),
    retry: false,
  });
  const operacao = useQuery({
    queryKey: ['operations', operationId],
    queryFn: ({ signal }) => operations.get(operationId, signal),
    enabled: !nova,
  });
  const termo = useQuery({
    queryKey: ['acceptance-term', 'cotacao'],
    queryFn: ({ signal }) => acceptanceTerms.current('cotacao', signal),
    retry: false,
  });
  // Os termos do passo Objeto, do mesmo cache do editor, para o aviso das TAGs vazias
  // ao avancar (NCT-133).
  const termosDoObjeto = useQuery({
    queryKey: chaveDosTermos(operationId),
    queryFn: ({ signal }) => contractualTerms.get(operationId, signal),
    enabled: !nova && passo === 'objeto',
  });

  // O rascunho nasce vazio na cotacao nova e da cotacao gravada na edicao. Trocar de
  // passo nao perde o que foi digitado: a rota troca e o componente continua montado.
  const [rascunho, setRascunho] = useState<RascunhoDaCotacao>(rascunhoVazio);
  const [carregadaDe, setCarregadaDe] = useState('');
  if (operacao.data && carregadaDe !== operacao.data.id) {
    setCarregadaDe(operacao.data.id);
    setRascunho({ ...rascunhoDaOperacao(operacao.data), declarou: rascunho.declarou });
  }
  function alterar(parcial: Partial<RascunhoDaCotacao>) {
    setRascunho((atual) => ({ ...atual, ...parcial }));
  }

  const termoVigente = termo.data ?? null;
  const taxaEditavel = opcoes.data?.rate_editable === true;
  const modalidade = opcoes.data?.modalities.find(
    (item) => item.id === rascunho.modality_id,
  );
  // A comissao do tomador so entra com a opcao ligada em Configuracao da cotacao
  // (OPEN_QUESTIONS 2.97): sem ela, nem se le o tomador para isso.
  const usaComissaoDoTomador = opcoes.data?.use_policyholder_commission === true;
  const tomadorDaComissao = useQuery({
    queryKey: ['policyholders', rascunho.policyholder_id],
    queryFn: ({ signal }) => policyholders.get(rascunho.policyholder_id, signal),
    enabled: usaComissaoDoTomador && rascunho.policyholder_id !== '',
  });
  const sugestaoDaComissao = corretora.data
    ? sugerirComissao(
        corretora.data,
        opcoes.data?.default_commission_bps,
        usaComissaoDoTomador ? tomadorDaComissao.data?.commission_bps : undefined,
      )
    : undefined;

  const caminho = (destino: PassoDoAssistente, id = operationId) =>
    id ? `/operacoes/${id}/editar/${destino}` : `/operacoes/nova/${destino}`;
  const indice = passosDoAssistente.findIndex((item) => item.key === passo);
  const anterior = passosDoAssistente[indice - 1]?.key;
  const seguinte = passosDoAssistente[indice + 1]?.key;

  // Ao fim do passo do risco a cotacao e gravada (nova) ou alterada (edicao); a
  // declaracao do passo do tomador vai junto, com a cotacao ja gravada.
  const gravar = useMutation({
    mutationFn: async () => {
      // A comissao sugerida vai so na abertura; na edicao, a gravada fica.
      const gravada = nova
        ? await operations.create(
            entradaDaAbertura(rascunho, modalidade, taxaEditavel, sugestaoDaComissao),
          )
        : await operations.update(
            operationId,
            entradaDaCotacao(rascunho, modalidade, taxaEditavel),
          );
      // A cotacao ja existe quando a declaracao e gravada: se a declaracao falhar, o
      // assistente segue na edicao da cotacao gravada, e o clique seguinte nao cria
      // outra (achado B22 da revisao das ondas 5 e 6).
      let declaracaoFalhou = false;
      if (termoVigente && rascunho.declarou) {
        try {
          await acceptanceTerms.approve(termoVigente.id, { operation_id: gravada.id });
        } catch {
          declaracaoFalhou = true;
        }
      }
      return { gravada, declaracaoFalhou };
    },
    onSuccess: ({
      gravada,
      declaracaoFalhou,
    }: {
      gravada: OperationDetail;
      declaracaoFalhou: boolean;
    }) => {
      toast.success(
        nova
          ? `Cotacao ${gravada.proposal_number ?? gravada.number} gerada.`
          : 'Cotacao atualizada.',
      );
      if (declaracaoFalhou) {
        toast.error(
          'A declaracao nao foi gravada. Marque-a de novo no primeiro passo antes de enviar a cotacao.',
        );
      }
      void queryClient.invalidateQueries({ queryKey: ['operations'] });
      void navigate(caminho('objeto', gravada.id));
    },
  });
  const apiError = gravar.error instanceof ApiError ? gravar.error : null;

  if (opcoes.isPending || (!nova && operacao.isPending)) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-8 w-64" />
        <Card>
          <CardBody>
            <SkeletonText lines={5} />
          </CardBody>
        </Card>
      </div>
    );
  }
  if (opcoes.isError || operacao.isError) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Nova cotacao" />
        <Alert tone="danger" title="Nao foi possivel abrir o assistente">
          {(opcoes.error ?? operacao.error)?.message}
        </Alert>
      </div>
    );
  }

  const op = operacao.data;
  if (op && op.quote_actions?.can_edit !== true) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title={`Cotacao ${op.proposal_number ?? op.number}`} />
        <Alert tone="info" title="Cotacao enviada para analise">
          <div className="flex flex-col gap-3">
            <p>
              A corretora altera a propria cotacao so antes do envio para analise.
              Depois dele, as mudancas sao feitas pela seguradora.
            </p>
            <Link
              to={`/operacoes/${op.id}`}
              className="font-medium text-brand-700 hover:underline"
            >
              Ver a cotacao
            </Link>
          </div>
        </Alert>
      </div>
    );
  }

  const faltas = faltasDoPasso(passo, rascunho, {
    termoVigente: termoVigente !== null,
    taxaEditavel,
  });
  const valor = parseMoney(rascunho.insured_amount);
  const estimativa: BrokerQuoteEstimateInput | null =
    passo === 'risco' && modalidade && valor && valor.amount > 0
      ? {
          modality_id: modalidade.id,
          insured_amount: valor,
          ...(taxaEditavel && rascunho.applied_rate.trim() !== ''
            ? { applied_rate_bps: rateToBasisPoints(rascunho.applied_rate) }
            : {}),
        }
      : null;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={
          op ? `Cotacao ${op.proposal_number ?? op.number}` : 'Nova cotacao Garantia'
        }
        description="Preencha os passos e envie a cotacao para analise da seguradora."
        actions={
          <Link
            to={op ? `/operacoes/${op.id}` : '/operacoes'}
            className={linkSecundario}
          >
            {op ? 'Ver a cotacao' : 'Voltar para cotacoes'}
          </Link>
        }
      />

      <PremiumEstimateBanner
        entrada={estimativa}
        gravado={
          op
            ? { premio: formatMoney(op.premium), taxa: formatRate(op.applied_rate_bps) }
            : undefined
        }
      />

      <Card>
        <CardBody>
          <Stepper
            label="Passos da cotacao"
            steps={passosDoAssistente.map((item) => ({
              key: item.key,
              label: item.label,
              disabledReason:
                !op && passoPedeCotacao(item.key)
                  ? 'Gere a cotacao no passo Dados do risco.'
                  : undefined,
            }))}
            currentKey={passo}
            onStepClick={(destino) =>
              void navigate(caminho(destino as PassoDoAssistente))
            }
          />
        </CardBody>
      </Card>

      {!op && passoPedeCotacao(passo) ? (
        <Alert tone="info" title="A cotacao ainda nao foi gerada">
          Este passo edita a cotacao gravada. Preencha os passos anteriores e gere a
          cotacao no passo Dados do risco.
        </Alert>
      ) : (
        <>
          {passo === 'tomador' ? (
            <TomadorStep
              rascunho={rascunho}
              alterar={alterar}
              apiError={apiError}
              termo={termoVigente}
            />
          ) : null}
          {passo === 'segurado' ? (
            <SeguradoStep rascunho={rascunho} alterar={alterar} apiError={apiError} />
          ) : null}
          {passo === 'risco' ? (
            <RiscoStep
              rascunho={rascunho}
              alterar={alterar}
              apiError={apiError}
              opcoes={opcoes.data}
              operacao={op}
            />
          ) : null}
          {passo === 'objeto' && op ? <ObjetoStep operacao={op} /> : null}
          {passo === 'pagamento' && op ? (
            <PagamentoStep
              operacao={op}
              opcoes={opcoes.data}
              sugestaoDaComissao={sugestaoDaComissao}
              corretora={corretora.data}
            />
          ) : null}
          {passo === 'resumo' && op ? (
            <ResumoStep
              operacao={op}
              opcoes={opcoes.data}
              termo={termoVigente}
              declarou={rascunho.declarou}
            />
          ) : null}
        </>
      )}

      {gravar.isError ? (
        <Alert tone="danger" title="Nao foi possivel gravar a cotacao">
          {gravar.error.message}
        </Alert>
      ) : null}

      {passo !== 'resumo' ? (
        <div className="flex flex-col items-end gap-2">
          {faltas.length > 0 ? (
            <p className="text-2xs text-ink-500">{faltas.join(' ')}</p>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            {anterior ? (
              <Button
                variant="secondary"
                onClick={() => void navigate(caminho(anterior))}
              >
                Voltar
              </Button>
            ) : null}
            {passo === 'risco' ? (
              <Button
                // Na abertura, a comissao sugerida espera a leitura da corretora.
                loading={gravar.isPending || (nova && corretora.isLoading)}
                disabled={
                  faltas.length > 0 ||
                  faltasAntes(rascunho, termoVigente !== null, taxaEditavel)
                }
                onClick={() => gravar.mutate()}
              >
                {nova ? 'Gerar cotacao' : 'Salvar e continuar'}
              </Button>
            ) : seguinte ? (
              <Button
                disabled={faltas.length > 0}
                onClick={() => {
                  // NCT-133: o aviso do OnPoint, sem travar o avanco nem o envio.
                  if (
                    passo === 'objeto' &&
                    temTagVaziaNoBlocoHabilitado(termosDoObjeto.data)
                  ) {
                    toast.info(AVISO_TAGS_NAO_PREENCHIDAS);
                  }
                  void navigate(caminho(seguinte));
                }}
              >
                Avancar
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Os passos do tomador e do segurado tambem precisam estar completos para gravar. */
function faltasAntes(
  rascunho: RascunhoDaCotacao,
  termoVigente: boolean,
  taxaEditavel: boolean,
): boolean {
  return (
    faltasDoPasso('tomador', rascunho, { termoVigente, taxaEditavel }).length > 0 ||
    faltasDoPasso('segurado', rascunho, { termoVigente, taxaEditavel }).length > 0
  );
}
